/**
 * 模块目录 —— Swift ModuleCatalog.swift 的 TypeScript 镜像。
 * 8 个一级模块、48 个二级主题，id/title/symbolName/pinyinSortKey 原样对应。
 * tint 颜色来自 iOS DesignSystem.swift 的 EncyclopediaModule.tint。
 */

import type { EntryKind } from './models';

export interface EncyclopediaModule {
  id: string;
  title: string;
  summary: string;
  symbolName: string;
  pinyinSortKey: string;
}

export interface EncyclopediaTopic {
  id: string;
  moduleID: string;
  title: string;
  symbolName: string;
  pinyinSortKey: string;
}

/** 模块主题色键，对应 Tailwind 主题色（见 tailwind.config.ts）。 */
export type ModuleTint = 'ocean' | 'teal' | 'indigo' | 'sage' | 'plum' | 'amber' | 'orange' | 'brown';

export const moduleTint: Record<string, ModuleTint> = {
  fishing: 'ocean',
  places: 'teal',
  systems: 'indigo',
  farming: 'sage',
  social: 'plum',
  tasks: 'amber',
  exploration: 'orange',
  items: 'brown',
};

export const modules: readonly EncyclopediaModule[] = [
  { id: 'fishing', title: '钓鱼', summary: '鱼类、地点、鱼饵、渔具与蟹笼', symbolName: 'fish.fill', pinyinSortKey: 'diaoyu' },
  { id: 'places', title: '地点与商店', summary: '区域、商店、营业时间与旅行目的地', symbolName: 'map.fill', pinyinSortKey: 'didian' },
  { id: 'systems', title: '基础与系统', summary: '季节、技能、时间、天气与核心机制', symbolName: 'gearshape.2.fill', pinyinSortKey: 'jichu' },
  { id: 'farming', title: '农场经营', summary: '作物、动物、建筑、加工与农场规划', symbolName: 'leaf.fill', pinyinSortKey: 'nongchang' },
  { id: 'social', title: '人物与社交', summary: '居民、送礼、友谊、婚姻与家庭', symbolName: 'person.2.fill', pinyinSortKey: 'renwu-1' },
  { id: 'tasks', title: '任务与收集', summary: '任务、收集包、特别订单与成就', symbolName: 'checklist', pinyinSortKey: 'renwu-2' },
  { id: 'exploration', title: '探索与战斗', summary: '矿井、洞穴、地牢、怪物与战斗', symbolName: 'figure.hiking', pinyinSortKey: 'tansuo' },
  { id: 'items', title: '物品与制作', summary: '工具、矿物、采集、打造、烹饪与装备', symbolName: 'shippingbox.fill', pinyinSortKey: 'wupin' },
] as const;

export const topics: readonly EncyclopediaTopic[] = [
  { id: 'places.town', moduleID: 'places', title: '城镇区域', symbolName: 'building.2.fill', pinyinSortKey: 'chengzhen' },
  { id: 'places.ginger-island', moduleID: 'places', title: '姜岛', symbolName: 'sun.max.fill', pinyinSortKey: 'jiangdao' },
  { id: 'places.desert', moduleID: 'places', title: '沙漠', symbolName: 'sun.haze.fill', pinyinSortKey: 'shamo' },
  { id: 'places.shops', moduleID: 'places', title: '商店', symbolName: 'storefront.fill', pinyinSortKey: 'shangdian' },
  { id: 'places.hours', moduleID: 'places', title: '营业时间', symbolName: 'clock.fill', pinyinSortKey: 'yingye' },

  { id: 'fishing.locations', moduleID: 'fishing', title: '钓鱼地点', symbolName: 'mappin.and.ellipse', pinyinSortKey: 'diaoyu' },
  { id: 'fishing.crab-pots', moduleID: 'fishing', title: '蟹笼', symbolName: 'basket.fill', pinyinSortKey: 'xielong' },
  { id: 'fishing.bait', moduleID: 'fishing', title: '鱼饵', symbolName: 'circle.hexagongrid.fill', pinyinSortKey: 'yuer' },
  { id: 'fishing.tackle', moduleID: 'fishing', title: '渔具', symbolName: 'wrench.adjustable.fill', pinyinSortKey: 'yuju' },
  { id: 'fishing.fish', moduleID: 'fishing', title: '鱼类', symbolName: 'fish.fill', pinyinSortKey: 'yulei' },

  { id: 'systems.seasons', moduleID: 'systems', title: '季节', symbolName: 'calendar', pinyinSortKey: 'jijie' },
  { id: 'systems.skills', moduleID: 'systems', title: '技能', symbolName: 'chart.bar.fill', pinyinSortKey: 'jineng' },
  { id: 'systems.controls', moduleID: 'systems', title: '控制与界面', symbolName: 'rectangle.on.rectangle', pinyinSortKey: 'kongzhi' },
  { id: 'systems.vitals', moduleID: 'systems', title: '能量与生命', symbolName: 'heart.fill', pinyinSortKey: 'nengliang' },
  { id: 'systems.time', moduleID: 'systems', title: '时间', symbolName: 'clock.fill', pinyinSortKey: 'shijian' },
  { id: 'systems.weather', moduleID: 'systems', title: '天气', symbolName: 'cloud.sun.fill', pinyinSortKey: 'tianqi' },
  { id: 'systems.luck', moduleID: 'systems', title: '运气', symbolName: 'sparkles', pinyinSortKey: 'yunqi' },

  { id: 'farming.animals', moduleID: 'farming', title: '动物', symbolName: 'pawprint.fill', pinyinSortKey: 'dongwu' },
  { id: 'farming.fruit-trees', moduleID: 'farming', title: '果树', symbolName: 'tree.fill', pinyinSortKey: 'guoshu' },
  { id: 'farming.artisan', moduleID: 'farming', title: '加工品', symbolName: 'gearshape.fill', pinyinSortKey: 'jiagong' },
  { id: 'farming.buildings', moduleID: 'farming', title: '建筑', symbolName: 'house.fill', pinyinSortKey: 'jianzhu' },
  { id: 'farming.maps', moduleID: 'farming', title: '农场地图', symbolName: 'map.fill', pinyinSortKey: 'nongchang' },
  { id: 'farming.selling', moduleID: 'farming', title: '售卖', symbolName: 'dollarsign.circle.fill', pinyinSortKey: 'shoumai' },
  { id: 'farming.greenhouse', moduleID: 'farming', title: '温室', symbolName: 'leaf.circle.fill', pinyinSortKey: 'wenshi' },
  { id: 'farming.crops', moduleID: 'farming', title: '作物', symbolName: 'carrot.fill', pinyinSortKey: 'zuowu' },

  { id: 'social.children', moduleID: 'social', title: '孩子', symbolName: 'figure.child', pinyinSortKey: 'haizi' },
  { id: 'social.marriage', moduleID: 'social', title: '婚姻', symbolName: 'heart.circle.fill', pinyinSortKey: 'hunyin' },
  { id: 'social.villagers', moduleID: 'social', title: '居民', symbolName: 'person.3.fill', pinyinSortKey: 'jumin' },
  { id: 'social.gifts', moduleID: 'social', title: '送礼', symbolName: 'gift.fill', pinyinSortKey: 'songli' },
  { id: 'social.friendship', moduleID: 'social', title: '友谊', symbolName: 'heart.fill', pinyinSortKey: 'youyi' },

  { id: 'tasks.achievements', moduleID: 'tasks', title: '成就', symbolName: 'trophy.fill', pinyinSortKey: 'chengjiu' },
  { id: 'tasks.bundles', moduleID: 'tasks', title: '收集包', symbolName: 'archivebox.fill', pinyinSortKey: 'shouji' },
  { id: 'tasks.special-orders', moduleID: 'tasks', title: '特别订单', symbolName: 'doc.badge.clock', pinyinSortKey: 'tebie' },
  { id: 'tasks.quests', moduleID: 'tasks', title: '任务', symbolName: 'checklist', pinyinSortKey: 'renwu' },

  { id: 'exploration.monsters', moduleID: 'exploration', title: '怪物', symbolName: 'ant.fill', pinyinSortKey: 'guaiwu' },
  { id: 'exploration.volcano', moduleID: 'exploration', title: '火山地牢', symbolName: 'flame.fill', pinyinSortKey: 'huoshan' },
  { id: 'exploration.mines', moduleID: 'exploration', title: '矿井', symbolName: 'mountain.2.fill', pinyinSortKey: 'kuangjing' },
  { id: 'exploration.skull-cavern', moduleID: 'exploration', title: '骷髅洞穴', symbolName: 'fossil.shell.fill', pinyinSortKey: 'kulou' },
  { id: 'exploration.combat', moduleID: 'exploration', title: '武器与战斗', symbolName: 'shield.lefthalf.filled', pinyinSortKey: 'wuqi' },

  { id: 'items.forage', moduleID: 'items', title: '采集', symbolName: 'leaf.circle.fill', pinyinSortKey: 'caiji' },
  { id: 'items.crafting', moduleID: 'items', title: '打造', symbolName: 'hammer.fill', pinyinSortKey: 'dazao' },
  { id: 'items.tools', moduleID: 'items', title: '工具', symbolName: 'wrench.and.screwdriver.fill', pinyinSortKey: 'gongju' },
  { id: 'items.artifacts', moduleID: 'items', title: '古物', symbolName: 'fossil.shell.fill', pinyinSortKey: 'guwu' },
  { id: 'items.furniture', moduleID: 'items', title: '家具', symbolName: 'chair.lounge.fill', pinyinSortKey: 'jiaju' },
  { id: 'items.minerals', moduleID: 'items', title: '矿物', symbolName: 'diamond.fill', pinyinSortKey: 'kuangwu' },
  { id: 'items.cooking', moduleID: 'items', title: '烹饪', symbolName: 'fork.knife', pinyinSortKey: 'pengren' },
  { id: 'items.trees', moduleID: 'items', title: '树木', symbolName: 'tree.fill', pinyinSortKey: 'shumu' },
  { id: 'items.equipment', moduleID: 'items', title: '装备', symbolName: 'shield.fill', pinyinSortKey: 'zhuangbei' },
] as const;

const modulesByID = new Map(modules.map((m) => [m.id, m]));
const topicsByID = new Map(topics.map((t) => [t.id, t]));

export function module(id: string): EncyclopediaModule | undefined {
  return modulesByID.get(id);
}

export function topic(id: string): EncyclopediaTopic | undefined {
  return topicsByID.get(id);
}

export function topicsForModule(moduleID: string): EncyclopediaTopic[] {
  return topics
    .filter((t) => t.moduleID === moduleID)
    .sort((a, b) => a.pinyinSortKey.localeCompare(b.pinyinSortKey));
}

/** 条目所属模块的 tint 键，未找到时返回 undefined。 */
export function moduleTintForEntry(topicID: string): ModuleTint | undefined {
  const t = topic(topicID);
  if (!t) return undefined;
  return moduleTint[t.moduleID];
}

/** EntryKind 缺省分类色（无 topic 时的 fallback），对应 iOS EntryIcon.categoryColor。 */
export const entryKindFallbackTint: Record<EntryKind, ModuleTint> = {
  character: 'plum',
  achievement: 'amber',
  equipment: 'indigo',
  furniture: 'brown',
  item: 'orange',
  crop: 'sage',
  fish: 'ocean',
  monster: 'orange',
  recipe: 'plum',
  quest: 'indigo',
  location: 'teal',
  mechanic: 'brown',
};
